import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Tooltip } from 'react-tippy'
import { isArray } from 'lodash'
import { CSSTransition } from 'react-transition-group'

import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'

import Icon, { NavArrowIcon } from '../../components/icon/Icon'
import GoogleSignIn from '../../components/buttons/GoogleSignIn'
import { MobileMenuButton } from '../../components/buttons/Buttons'
import { Desktop, SmallDesktop } from '../../utils/media'
import {
  translate,
  pushWithLanguageRoutes,
  replaceWithLanguageRoutes,
  getLanguageCode,
} from '../../utils/translation'
import {
  isAdminUser,
  isEnterpriseAdmin,
  isSignedIn,
  signOut,
} from '../../utils/userAuth'
import { getPricingURL } from '../../utils/consts.mjs'
import HamburgerMenu from '../../styles/assets/sidebarIcons/hamburger_menu.svg'
import logo from '../../styles/assets/ubersuggest-logo-orange.svg'
import avatar from '../../styles/assets/avatar.svg'
import paymentIcon from '../../styles/assets/payment.svg'
import exit from '../../styles/assets/exit.svg'
import alarmBlack from '../../styles/assets/alarm_black.svg'
import peopleIcon from '../../styles/assets/pricing/people.svg'
import peopleLightTheme from '../../styles/assets/people_light_theme.svg'
import { isPaidUser } from '../../utils/freeVersionBlock'
import { MIXPANEL_PROPS, Mixpanel } from '../../utils/mixpanel'
import {
  KISSMETRICS_TRACK_EVENTS,
  PROPERTY_NAMES,
  PROPERTY_VALUES,
  kissmetricsRecordingEvent,
} from '../../utils/kissmetrics'
import { ButtonLink, Link } from '../../components/topography'
import { mediaQueries, palette } from '../../styles/variables'
import AppSelector from '../../components/selector/AppSelector'
import LanguageSelector from '../../components/selector/language-selector'
import { selectCurrentUser } from '../../state/selectors'
import MegaMenu from '../../components/megaMenu'
import { OnboardingContext } from '../../features/onboarding/context'

import {
  NavBarWrapper,
  NavLogo,
  NavLogoImg,
  NavLogoButton,
  PricingButton,
  NavButtonsWrapper,
  NavItem,
  RightMenu,
  SettingsPanel,
  SettingItem,
  SettingsButton,
  Pulse,
  SettingsIcon,
  AlertButton,
  AlertPopups,
  ServicesMenuWrapper,
} from './styled'

const userImageStyle = (isPro) => ({
  objectFit: 'cover',
  borderRadius: '50%',
  width: 35,
  height: 'auto',
  border: isPro ? '3px solid gold' : '0',
})

const TIME_TO_CLOSE = 500
class NavBar extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      hideTooltip: true,
      userLoaded: false,
      openAlert: false,
      isServicesOpen: false,
      isAccountOpen: false,
      isMouseInsideMenu: false,
      isMouseInsideButton: false,
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!nextProps.loadingUser === prevState.userLoaded) {
      return {
        hideTooltip: prevState.hideTooltip || nextProps.hideTooltip,
      }
    }

    if (nextProps.user && !nextProps.user.isAnnonymous) {
      return {
        userLoaded: !nextProps.loadingUser,
        hideTooltip: true,
      }
    }

    if (window.location.pathname.indexOf('dashboard') < 0) {
      return {
        userLoaded: !nextProps.loadingUser,
        hideTooltip: nextProps.hideTooltip || false,
      }
    }
  }

  onSignOut = (e) => {
    e.preventDefault()
    signOut()
  }

  clickNotification = (alert, asRead = true) => {
    if (alert.alertType === 'onboarding_survey_alert') {
      const { openModal } = this.context
      openModal('notification')
      return
    }

    if (alert.alertType === 'keywords_setup_alert') {
      pushWithLanguageRoutes(
        `/dashboard?projectId=${alert.projectId}&action=open_keywords_modal`,
      )
      return
    }

    if (alert.alertType === 'competitors_setup_alert') {
      pushWithLanguageRoutes(
        `/dashboard?projectId=${alert.projectId}&action=open_competitors_modal`,
      )
      return
    }

    if (!alert.seen && alert.alertType !== 'default') {
      const alertIds = [alert.alertId]
      this.props.markAlertsAs(alertIds, asRead ? 'seen' : 'unseen')
    }
    if (alert.alertType === 'position_tracking' && alert.content.report_type) {
      pushWithLanguageRoutes(`/alerts/${alert.alertId}`)
    } else if (alert.alertType === 'site_audit_report_created') {
      pushWithLanguageRoutes(
        `/seo_analyzer/site_audit?domain=${alert.content.domain}&action=crawl`,
      )
    } else {
      pushWithLanguageRoutes('/alerts')
    }
  }

  handleMouseEnterButton = (stateProp) => {
    this.setState({
      openAlert: false,
      isServicesOpen: false,
      isAccountOpen: false,
      isMouseInsideButton: true,
      [stateProp]: true,
    })
  }

  handleMouseLeaveButton = (stateProp) => {
    this.setState({ isMouseInsideButton: false })
    setTimeout(() => {
      if (!this.state.isMouseInsideButton && !this.state.isMouseInsideMenu) {
        this.setState({ [stateProp]: false })
      }
    }, TIME_TO_CLOSE)
  }

  render() {
    const {
      collapsed,
      onToggleCollapse,
      user,
      alerts,
      loadingUser,
      children,
      closeSiteAuditReportAlert,
    } = this.props

    const { isServicesOpen, isAccountOpen, openAlert, isMouseInsideButton } =
      this.state
    const languageCode = getLanguageCode()
    const shouldHideButtons =
      window.location.pathname.indexOf('/pricing') >= 0 ||
      window.location.pathname.indexOf('/verify-email') >= 0
    const alertBadgeCount = isArray(alerts)
      ? alerts.reduce(
          (count, a) => count + (a.alertType === 'default' || a.seen ? 0 : 1),
          0,
        )
      : 0

    const isFreeTrialUsed = user?.free_trial_used

    return (
      <NavBarWrapper>
        <NavLogo>
          <NavLogoImg
            data-testid="nav-logo"
            src={logo}
            alt="Logo"
            onClick={() => replaceWithLanguageRoutes('/dashboard')}
          />
          <LanguageSelector />
          <Desktop minWidth={1281}>
            {!shouldHideButtons && <AppSelector />}
          </Desktop>
        </NavLogo>
        <Desktop minWidth={1281}>
          <NavButtonsWrapper>
            {isSignedIn() ? (
              <RightMenu>
                <Tooltip
                  open={openAlert}
                  arrow
                  interactive
                  distance={-5}
                  theme="light"
                  trigger="mouseenter"
                  hideOnClick={false}
                  onHide={() => this.setState({ openAlert: false })}
                  onShow={() =>
                    this.setState({
                      openAlert: true,
                      isServicesOpen: false,
                      isAccountOpen: false,
                    })
                  }
                  html={
                    <div style={{ backgroundColor: palette.white }}>
                      <AlertPopups
                        alerts={alerts}
                        onClick={(alert) => {
                          this.setState({ openAlert: false })
                          this.clickNotification(alert, true)
                        }}
                        onClickAll={() => {
                          this.setState({ openAlert: false })
                          pushWithLanguageRoutes('/alerts')
                          kissmetricsRecordingEvent(
                            KISSMETRICS_TRACK_EVENTS.actions_alerts,
                            {
                              [PROPERTY_NAMES.alerts_clicks]:
                                PROPERTY_VALUES.alerts_clicks.see_all_alerts,
                            },
                          )
                        }}
                      />
                    </div>
                  }
                  onShown={() => {
                    kissmetricsRecordingEvent(
                      KISSMETRICS_TRACK_EVENTS.actions_alerts,
                      {
                        [PROPERTY_NAMES.alerts_clicks]:
                          PROPERTY_VALUES.alerts_clicks.clicked_bell_alert,
                      },
                    )
                  }}
                  style={{
                    padding: '20px',
                    display: 'inline-block',
                  }}
                >
                  <AlertButton
                    badgeCount={alertBadgeCount}
                    onClick={() => {
                      closeSiteAuditReportAlert()
                    }}
                    hasArrow
                    cursor="default"
                    arrowDirection={openAlert ? 'up' : 'down'}
                    arrowColor={openAlert ? palette.orange : palette.gray}
                  />
                </Tooltip>
                {/* { This is for showing site audit report ready alert} */}
                {children}
                {!shouldHideButtons && (
                  <>
                    <div
                      className="tw-cursor-pointer tw-p-5 tw-inline-block"
                      role="button"
                      tabIndex={0}
                      onMouseEnter={() =>
                        this.setState({ isMouseInsideButton: true })
                      }
                      onMouseLeave={() =>
                        this.setState({ isMouseInsideButton: false })
                      }
                      onClick={() =>
                        this.setState({ isServicesOpen: !isServicesOpen })
                      }
                      onKeyDown={() =>
                        this.setState({ isServicesOpen: !isServicesOpen })
                      }
                    >
                      <PricingButton isOpen={isServicesOpen}>
                        {translate('services')}
                        <NavArrowIcon
                          up={isServicesOpen}
                          down={!isServicesOpen}
                          color={palette[isServicesOpen ? 'orange' : 'gray']}
                        />
                      </PricingButton>
                    </div>

                    <ButtonLink
                      onClick={() => {
                        Mixpanel.savePurchaseClickPageName(
                          MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values
                            .PRICE_PAGE,
                        )
                        trackEvent({
                          event: EVENTS.PURCHASE_CLICK,
                          mixpanelProperties: [
                            MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values
                              .PRICE_PAGE,
                            isFreeTrialUsed
                              ? MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE
                              : MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
                            MIXPANEL_PROPS.SOURCE.values.PRICING_PAGE,
                          ],
                          kissmetricsProperties: {
                            [PROPERTY_NAMES.headlinks_link]:
                              PROPERTY_VALUES.headlinks_link.plans_pricing,
                          },
                        })

                        pushWithLanguageRoutes(
                          getPricingURL({ lang: languageCode }),
                        )
                      }}
                      style={{ margin: 12 }}
                    >
                      <PricingButton>
                        {translate('plans_pricing')}
                      </PricingButton>
                    </ButtonLink>
                  </>
                )}
                {isEnterpriseAdmin() && (
                  <Link to={`/${languageCode}/enterprise/dashboard`}>
                    <div
                      style={{
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        background: '#F16434',
                        border: '2px solid #F16434',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '29px',
                      }}
                    >
                      <SettingsIcon
                        width={24}
                        src={peopleLightTheme}
                        alt="enterprise dashboard"
                      />
                    </div>
                  </Link>
                )}
                <NavLogoButton
                  cursor="default"
                  data-testid="nav-logo-button"
                  onMouseEnter={() =>
                    this.handleMouseEnterButton('isAccountOpen')
                  }
                  onMouseLeave={() =>
                    this.handleMouseLeaveButton('isAccountOpen')
                  }
                >
                  <img
                    src={(user && user.picture) || avatar}
                    alt="user-pic"
                    style={userImageStyle(isPaidUser())}
                    type="image/png"
                    ref={(ref) => {
                      this.userAvatar = ref
                    }}
                    onError={() => {
                      this.userAvatar.src = avatar
                    }}
                  />
                  <NavArrowIcon
                    up={isAccountOpen}
                    down={!isAccountOpen}
                    color={palette[isAccountOpen ? 'orange' : 'gray']}
                  />
                  <CSSTransition
                    in={isAccountOpen}
                    timeout={300}
                    classNames="top-bottom"
                    unmountOnExit
                  >
                    <SettingsPanel>
                      <SettingItem>
                        <Link to={`/${languageCode}/settings/account_billing`}>
                          <SettingsButton>
                            <SettingsIcon
                              width={16}
                              src={paymentIcon}
                              alt="account_biling"
                            />
                            {translate('account_billing_tab')}
                          </SettingsButton>
                        </Link>
                      </SettingItem>
                      <SettingItem>
                        <Link to={`/${languageCode}/settings/notifications`}>
                          <SettingsButton>
                            <SettingsIcon
                              width={16}
                              src={alarmBlack}
                              alt="notification"
                            />
                            {translate('manage_notifications')}
                          </SettingsButton>
                        </Link>
                      </SettingItem>
                      {isAdminUser() && (
                        <SettingItem>
                          <Link to={`/${languageCode}/admin/manage`}>
                            <SettingsButton>
                              <SettingsIcon
                                width={16}
                                src={peopleIcon}
                                alt="admin"
                              />
                              {translate('admin_area')}
                            </SettingsButton>
                          </Link>
                        </SettingItem>
                      )}
                      <SettingItem>
                        <SettingsButton
                          data-testid="sing-out"
                          onClick={this.onSignOut}
                        >
                          <SettingsIcon width={16} src={exit} alt="signOut" />
                          {translate('sign_out')}
                        </SettingsButton>
                      </SettingItem>
                    </SettingsPanel>
                  </CSSTransition>
                </NavLogoButton>
              </RightMenu>
            ) : (
              !shouldHideButtons && (
                <NavItem>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 28,
                    }}
                  >
                    <div
                      className="tw-cursor-pointer tw-p-5 tw-inline-block"
                      role="button"
                      tabIndex={0}
                      onMouseEnter={() =>
                        this.setState({ isMouseInsideButton: true })
                      }
                      onMouseLeave={() =>
                        this.setState({ isMouseInsideButton: false })
                      }
                      onClick={() =>
                        this.setState({ isServicesOpen: !isServicesOpen })
                      }
                      onKeyDown={() =>
                        this.setState({ isServicesOpen: !isServicesOpen })
                      }
                    >
                      <PricingButton isOpen={isServicesOpen}>
                        {translate('services')}
                        <NavArrowIcon
                          up={isServicesOpen}
                          down={!isServicesOpen}
                          color={palette[isServicesOpen ? 'orange' : 'gray']}
                        />
                      </PricingButton>
                    </div>
                    <ButtonLink
                      data-testid="nav-plans-pricing-link"
                      onClick={() => {
                        trackEvent({
                          event: EVENTS.PURCHASE_CLICK,
                          mixpanelProperties: [
                            MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values
                              .PRICE_PAGE,
                            isFreeTrialUsed
                              ? MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE
                              : MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
                            MIXPANEL_PROPS.SOURCE.values.PRICING_PAGE,
                          ],
                        })
                        pushWithLanguageRoutes(
                          getPricingURL({ lang: languageCode }),
                        )
                      }}
                    >
                      <PricingButton>
                        {translate('plans_pricing')}
                      </PricingButton>
                    </ButtonLink>
                    {loadingUser ? (
                      <Pulse />
                    ) : (
                      <GoogleSignIn
                        testId="nav-sign-in-button"
                        hideIcon
                        useClickEvent
                        redirectURL="/login"
                        label={translate('sign_in')}
                        height="34px"
                        theme="dark"
                        onClick={() => {
                          kissmetricsRecordingEvent(
                            KISSMETRICS_TRACK_EVENTS.clicked_signin,
                            {
                              [PROPERTY_NAMES.signin_location]:
                                PROPERTY_VALUES.signin_location.top_bar,
                            },
                          )
                        }}
                      />
                    )}
                  </div>
                </NavItem>
              )
            )}
          </NavButtonsWrapper>
        </Desktop>
        <SmallDesktop>
          <AlertButton
            badgeCount={alertBadgeCount}
            onClick={() => pushWithLanguageRoutes('/alerts')}
          />
          {!shouldHideButtons && !collapsed && (
            <MobileMenuButton
              styles={{
                [mediaQueries.smallDesktop]: {
                  top: '17px',
                  zIndex: 999,
                },
              }}
              onClick={onToggleCollapse}
            >
              <Icon size={28} src={HamburgerMenu} />
            </MobileMenuButton>
          )}
        </SmallDesktop>
        <CSSTransition
          in={isServicesOpen}
          timeout={300}
          classNames="top-bottom"
          unmountOnExit
        >
          <ServicesMenuWrapper>
            <MegaMenu
              handleClose={() => this.setState({ isServicesOpen: false })}
              isMouseInsideButton={isMouseInsideButton}
            />
          </ServicesMenuWrapper>
        </CSSTransition>
      </NavBarWrapper>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  user: selectCurrentUser(),
})

NavBar.contextType = OnboardingContext

export default connect(mapStateToProps)(NavBar)
